import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import {
  SECURITY_SCAN_ERROR_LABELS,
  SecuritySeverities,
  messages,
  type SecurityCheck,
  type SecurityScanErrorCode,
} from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfidenceMark } from '@/components/common/evidence';
import { formatDateTime, recommendationFor } from '@nesc/shared';

type Finding = SecurityCheck['findings'][number];

/** 企業詳細・セキュリティタブ（F12） */
export const SecurityPanel = ({ companyId, websiteUrl }: { companyId: string; websiteUrl: string | null }) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: qk.companySecurityCheck(companyId),
    queryFn: () => api.get<{ data: SecurityCheck }>(`/companies/${companyId}/security-check`),
    // 未実施（404）も通常状態のため再試行しない
    retry: false,
  });

  const scan = useMutation({
    mutationFn: () => api.post(`/companies/${companyId}/security-scan`),
    onSuccess: () => {
      toast.success(messages.company.securityScanStarted, { description: '完了すると結果が表示されます' });
      void queryClient.invalidateQueries({ queryKey: qk.companySecurityCheck(companyId) });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const check = data?.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield aria-hidden className="size-5" />
          {messages.company.securityScan}
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => scan.mutate()}
          disabled={scan.isPending || !websiteUrl}
          title={websiteUrl ? undefined : '対象 URL が未設定です'}
        >
          {scan.isPending ? <Loader2 aria-hidden className="size-4 animate-spin" /> : null}
          {check ? messages.company.securityRescan : messages.company.securityScan}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-note text-foreground-muted">{messages.company.securityScanNote}</p>

        {!check && <p className="text-body text-foreground-muted">{messages.company.securityNotRun}</p>}

        {check && <SecurityResult check={check} />}
      </CardContent>
    </Card>
  );
};

const SecurityResult = ({ check }: { check: SecurityCheck }) => {
  const findings = check.findings as Finding[];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge
          kind="security_risk"
          value={check.riskLevel}
          size="lg"
          emphasis={check.riskLevel === 'high' ? 'high' : undefined}
        />
        <ConfidenceMark level={check.confidence} />
        <span className="tabular text-note text-foreground-muted">
          実施 <span>{formatDateTime(check.checkedAt)}</span>
        </span>
      </div>

      {check.errorCode && (
        <p className="text-body text-foreground-muted">
          {SECURITY_SCAN_ERROR_LABELS[check.errorCode as SecurityScanErrorCode] ?? check.errorMessage}
        </p>
      )}

      <p className="whitespace-pre-wrap text-body">{check.summary}</p>

      {findings.length > 0 && (
        <div>
          <h3 className="mb-2 text-body font-semibold">{messages.company.securityFindings}</h3>
          <ul className="divide-y divide-border">
            {findings.map((finding, index) => (
              <li key={index} className="space-y-1 py-2.5">
                <p className="flex flex-wrap items-center gap-2 text-body">
                  <Badge variant={finding.severity === 'high' ? 'solid' : 'default'}>
                    {SecuritySeverities.label(finding.severity)}
                  </Badge>
                  <span className="font-semibold">{finding.title}</span>
                </p>
                {finding.evidence && (
                  <p className="break-all font-mono text-note text-foreground-muted">{finding.evidence}</p>
                )}
                {finding.note && <p className="text-note text-foreground-muted">{finding.note}</p>}
                <p className="text-note">
                  <span className="text-foreground-muted">{messages.company.securityRecommendation}：</span>
                  {recommendationFor(finding)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {check.salesHook && (
        <div>
          <h3 className="mb-1 text-body font-semibold">{messages.company.securitySalesHook}</h3>
          <p className="text-body text-foreground-muted">{check.salesHook}</p>
        </div>
      )}

      {(check.assumptions as string[]).length > 0 && (
        <div>
          <h3 className="mb-1 text-note font-semibold">推測を含む項目</h3>
          <ul className="list-inside list-disc text-note text-foreground-muted">
            {(check.assumptions as string[]).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <ServiceOverview check={check} />
      <Details check={check} />
    </div>
  );
};

/** サービスの全体像とサイト構成（受動的な静的解析の要点）。根拠は Details に生値がある */
type ProfileOverview = {
  platform: string | null;
  techStack: string[];
  frameworks: string[];
  hasLogin: boolean;
  ecommerce: boolean;
  externalScriptOrigins: string[];
  formCount: number;
  sourceMapsExposed: boolean;
  siteStructure: {
    robotsPresent: boolean;
    sitemapUrlCount: number | null;
    securityTxt: 'present' | 'expired' | 'absent';
  } | null;
};

/** インフラの推定（公開 DNS と応答の痕跡から。2026-09-26 より前の結果には無い） */
type InfraGuess = { provider: string; service: string | null; evidence: string };
type InfraSignals = {
  hosting: InfraGuess | null;
  cdn: InfraGuess | null;
  waf: InfraGuess | null;
  dnsProvider: string | null;
  mailProvider: string | null;
};

const guessLabel = (g: InfraGuess | null, none: string): string =>
  g ? `${g.provider}${g.service ? `（${g.service}）` : ''}` : none;

const SECURITY_TXT_LABEL: Record<'present' | 'expired' | 'absent', string> = {
  present: 'あり',
  expired: '期限切れ',
  absent: 'なし',
};

const ServiceOverview = ({ check }: { check: SecurityCheck }) => {
  const signals = check.signals as { profile?: { overview?: ProfileOverview } | null; infra?: InfraSignals | null };
  const overview = signals.profile?.overview;
  const infra = signals.infra;
  if (!overview) return null;

  const tech = [...new Set([...overview.frameworks, ...overview.techStack])].slice(0, 6);
  const structure = overview.siteStructure;

  return (
    <div>
      <h3 className="mb-1 text-body font-semibold">サービスの全体像</h3>
      <dl className="space-y-1 text-note">
        <Row label="プラットフォーム" value={overview.platform ?? '—'} />
        {infra && (
          <>
            <Row label="ホスティング" value={guessLabel(infra.hosting, '推定できず')} />
            <Row label="CDN" value={guessLabel(infra.cdn, '痕跡なし')} />
            <Row label="WAF" value={guessLabel(infra.waf, '痕跡なし（未導入とは限らない）')} />
            <Row label="DNS / メール" value={`${infra.dnsProvider ?? '—'} / ${infra.mailProvider ?? '—'}`} />
          </>
        )}
        {tech.length > 0 && <Row label="技術" value={tech.join('、')} />}
        <Row
          label="導線"
          value={[overview.hasLogin ? 'ログイン/会員あり' : null, overview.ecommerce ? '購入/EC あり' : null]
            .filter(Boolean)
            .join('、') || '—'}
        />
        <Row label="フォーム数" value={`${overview.formCount} 件`} />
        <Row label="外部スクリプトの読み込み元" value={`${overview.externalScriptOrigins.length} ドメイン`} />
        <Row label="ソースマップの公開" value={overview.sourceMapsExposed ? 'あり' : 'なし'} />
        {structure && (
          <Row
            label="サイト構成"
            value={`robots.txt ${structure.robotsPresent ? 'あり' : 'なし'} / sitemap ${
              structure.sitemapUrlCount != null ? `${structure.sitemapUrlCount} 件` : 'なし'
            } / 届け出先(security.txt) ${SECURITY_TXT_LABEL[structure.securityTxt]}`}
          />
        )}
      </dl>
    </div>
  );
};

/** 収集した信号の要点（根拠の確認用） */
const Details = ({ check }: { check: SecurityCheck }) => {
  const signals = check.signals as {
    url?: string;
    finalUrl?: string;
    httpStatus?: number | null;
    headers?: Record<string, string | null>;
    tls?: { protocol?: string; cipher?: string; certDaysRemaining?: number | null } | null;
    cookies?: string[];
    redirects?: { status: number; location: string }[];
  };

  const headerEntries = Object.entries(signals.headers ?? {});
  if (!signals.url && headerEntries.length === 0 && !signals.tls) return null;

  return (
    <details className="rounded-card border border-border">
      <summary className="cursor-pointer px-4 py-2.5 text-body font-semibold">
        {messages.company.securitySignals}
      </summary>
      <div className="space-y-3 px-4 pb-4 text-note">
        <dl className="space-y-1">
          <Row label="最終 URL" value={signals.finalUrl ?? signals.url ?? '—'} />
          <Row label="HTTP ステータス" value={signals.httpStatus != null ? String(signals.httpStatus) : '—'} />
          {signals.tls && (
            <>
              <Row label="TLS" value={`${signals.tls.protocol ?? '—'} / ${signals.tls.cipher ?? '—'}`} />
              <Row
                label="証明書の残り"
                value={signals.tls.certDaysRemaining != null ? `${signals.tls.certDaysRemaining} 日` : '—'}
              />
            </>
          )}
          {(signals.redirects ?? []).map((redirect, index) => (
            <Row key={index} label={`リダイレクト ${redirect.status}`} value={redirect.location} />
          ))}
        </dl>
        {headerEntries.length > 0 && (
          <div>
            <p className="mb-1 font-semibold">レスポンスヘッダ</p>
            <ul className="space-y-0.5 font-mono">
              {headerEntries.map(([name, value]) => (
                <li key={name} className="break-all">
                  {name}: {value ?? '（なし）'}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(signals.cookies ?? []).length > 0 && (
          <div>
            <p className="mb-1 font-semibold">Set-Cookie</p>
            <ul className="space-y-0.5 font-mono">
              {(signals.cookies ?? []).map((cookie, index) => (
                <li key={index} className="break-all">
                  {cookie}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5 lg:flex-row lg:gap-3">
    <dt className="w-36 shrink-0 text-foreground-muted">{label}</dt>
    <dd className="min-w-0 flex-1 break-all">{value}</dd>
  </div>
);
